import {
    Alignment,
    Component,
    Material,
    Object3D,
    TextComponent,
    TextEffect,
    VerticalAlignment,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';
import {gameState} from '../state/GameState.js';
import {AxisY} from '../utils/math.js';

const tempVec = vec3.create();

function swap<T>(a: Array<T> | Float32Array, i: number, j: number) {
    [a[i], a[j]] = [a[j], a[i]];
}

/**
 * damage-text-particles
 */
export class DamageTextParticles extends Component {
    static TypeName = 'damage-text-particles';

    /* Properties that are configurable in the editor */

    @property.material({required: true})
    textMaterial!: Material;

    @property.int(50)
    count: number = 50;

    pool: Object3D[] = [];
    textComp: TextComponent[] = [];
    startTime!: Float32Array;
    random!: Float32Array;

    activeCount = 0;

    start() {
        this.pool = this.scene.addObjects(this.count, null, this.count);
        this.startTime = new Float32Array(this.count);
        this.random = new Float32Array(this.count);

        this.pool.forEach((o, i) => {
            this.textComp[i] = o.addComponent('text', {
                alignment: Alignment.Center,
                verticalAlignment: VerticalAlignment.Middle,
                material: this.textMaterial.clone(),
                text: '0000',
                effect: TextEffect.Outline,
                active: false,
            });
        });

        gameState.onAttack.add(this.emit.bind(this));
    }

    time = 0;
    lastEmit = 0;

    update(dt: number) {
        this.time += dt;
        const MAX_LIFE = 2;
        /* Iterate in reverse such that we can remove
         * end-of-life particles at the end */
        for (let i = this.activeCount - 1; i >= 0; --i) {
            const t = this.time - this.startTime[i];
            const o = this.pool[i];
            const r = this.random[i];

            o.translateLocal([
                dt * 3 * r,
                dt * (-t * t + 1),
                /* Make sure newer damage numbers are further front */
                -dt * 0.1,
            ]);
            const s = 10 * (1 - Math.max(0, t - 1));
            o.setScalingLocal([s, s, s]);

            const gb = 1 - (t / MAX_LIFE) * (t / MAX_LIFE);
            this.textComp[i].material.setColor([1, gb, gb, 1]);

            if (t > MAX_LIFE) this.returnToPool(i);
        }
    }

    returnToPool(i: number) {
        this.pool[i].active = false;

        /* Swap the element to remove to last active */
        const last = this.activeCount - 1;
        if (i !== last) {
            swap(this.pool, i, last);
            swap(this.textComp, i, last);
            swap(this.startTime, i, last);
            swap(this.random, i, last);
        }

        /* Cut off the last active element */
        --this.activeCount;
    }

    emit(v: number, pos: vec3) {
        /* Out of pooled particles */
        if (this.activeCount >= this.count) return;

        this.startTime[this.activeCount] = this.time;
        this.random[this.activeCount] = Math.random() - 0.5;
        const o = this.pool[this.activeCount];
        o.active = true;
        const t = o.getComponent('text')!;
        t.text = v.toString();

        o.setPositionLocal(pos);
        o.setScalingLocal([10, 10, 10]);
        o.lookAt(this.engine.scene.activeViews[0].object.getPositionWorld(tempVec));
        o.rotateAxisAngleDegObject(AxisY, 180);

        t.material!.setColor([1, 1, 1, 1]);

        ++this.activeCount;
    }
}
